import React, { createContext, useContext, useState, useCallback } from "react"
import { Snackbar, Alert, AlertTitle } from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import useDeviceType from "../utils/useDeviceType"

const NotificationContext = createContext()

const DEFAULT_NOTIFICATION = {
  open: false,
  severity: "success",
  title: "",
  message: "",
  autoHideDuration: 2000,
}

const MUI_SEVERITIES = ["success", "error", "warning", "info"]

const NOTIFICATION_STYLES = {
  success: {
    backgroundColor: "#d1fadf",
    color: "#0f5132",
    border: "1px solid #a3e4c1",
    icon: <CheckIcon sx={{ color: "#0f5132" }} />,
    closeColor: "#0f5132",
  },
  error: {
    backgroundColor: "#FFF2F2",
    color: "#DD1E1E",
    border: "1px solid #FFB4B4",
    icon: <ErrorOutlineIcon sx={{ color: "#DD1E1E" }} />,
    closeColor: "#DD1E1E",
  },
  warning: {
    backgroundColor: "#fff4cc",
    color: "#8a6300",
    border: "1px solid #ffe08a",
    icon: <WarningAmberIcon sx={{ color: "#8a6300" }} />,
    closeColor: "#8a6300",
  },
  info: {
    backgroundColor: "#e6f2ff",
    color: "#1e4f8a",
    border: "1px solid #b8d6ff",
    icon: <InfoOutlinedIcon sx={{ color: "#1e4f8a" }} />,
    closeColor: "#1e4f8a",
  },
  accessDenied: {
    backgroundColor: "#f9f4eb",
    color: "#e7660d",
    border: "1px solid #f2e8d8",
    icon: <InfoOutlinedIcon sx={{ color: "#e7660d" }} />,
    closeColor: "#e7660d",
  },
}

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(DEFAULT_NOTIFICATION)

  const { isMobile } = useDeviceType()

  const showNotification = useCallback((payload = {}) => {
    const {
      severity = "success",
      title = "",
      message = "",
      autoHideDuration = 2000,
    } = payload

    setNotification({
      open: true,
      severity,
      title,
      message,
      autoHideDuration,
    })
  }, [])

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") return
    setNotification((prev) => ({ ...prev, open: false }))
  }, [])

  const variantStyle =
    NOTIFICATION_STYLES[notification.severity] || NOTIFICATION_STYLES.info
  const muiSeverity = MUI_SEVERITIES.includes(notification.severity)
    ? notification.severity
    : "info"
  const isAccessDenied = notification.severity === "accessDenied"

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      <Snackbar
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={
          isMobile
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "top", horizontal: "right" }
        }
        sx={
          isMobile && {
            "&.MuiSnackbar-root": {
              right: "20px",
              bottom: "40px",
            },
          }
        }
      >
        <Alert
          onClose={handleClose}
          severity={muiSeverity}
          variant="outlined"
          icon={variantStyle.icon}
          sx={{
            maxWidth: 360,
            backgroundColor: variantStyle.backgroundColor,
            color: variantStyle.color,
            border: variantStyle.border,
          }}
        >
          {notification.title && (
            <AlertTitle
              sx={{
                fontWeight: 700,
                color: variantStyle.color,
                mb: isAccessDenied ? 0.5 : 0,
              }}
            >
              {notification.title}
            </AlertTitle>
          )}
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    )
  }
  return context
}

/**
 * ==============================================
 * Notification Context
 *
 * This file provides a global notification system
 * using React Context + MUI Snackbar/Alert.
 *
 * - Wrap your app with <NotificationProvider> in App.js:
 *
 *   <NotificationProvider>
 *     <App />
 *   </NotificationProvider>
 *
 * - Use the hook anywhere inside the provider:
 *
 *   import { useNotification } from "../context/NotificationContext"
 *
 *   const { showNotification } = useNotification()
 *
 *   showNotification({
 *     severity: "success",   // "success" | "error" | "warning" | "info" | "accessDenied"
 *     title: "Saved!",
 *     message: "Your changes have been saved successfully.",
 *     autoHideDuration: 3000, // optional, defaults to 2000
 *   })
 *
 * This will show a styled Snackbar notification.
 * ==============================================
 */
